/**
 * Exact rational arithmetic on `bigint` fractions, for balancing equations and any other
 * small linear system where a floating-point null space would give 0.333… instead of 1/3.
 */

const B0 = BigInt(0);
const B1 = BigInt(1);

export interface Fraction {
  n: bigint;
  d: bigint;
}

export function gcdBig(a: bigint, b: bigint): bigint {
  let x = a < B0 ? -a : a;
  let y = b < B0 ? -b : b;
  while (y !== B0) {
    [x, y] = [y, x % y];
  }
  return x;
}

export function lcmBig(a: bigint, b: bigint): bigint {
  if (a === B0 || b === B0) {
    return B0;
  }
  return (a / gcdBig(a, b)) * b;
}

export function frac(n: bigint | number, d: bigint | number = B1): Fraction {
  let num = BigInt(n);
  let den = BigInt(d);
  if (den === B0) {
    throw new RangeError("zero denominator");
  }
  if (den < B0) {
    num = -num;
    den = -den;
  }
  const g = gcdBig(num, den);
  return g > B1 ? { n: num / g, d: den / g } : { n: num, d: den };
}

export const ZERO: Fraction = { n: B0, d: B1 };
export const ONE: Fraction = { n: B1, d: B1 };

export function isZero(a: Fraction): boolean {
  return a.n === B0;
}

export function add(a: Fraction, b: Fraction): Fraction {
  return frac(a.n * b.d + b.n * a.d, a.d * b.d);
}

export function sub(a: Fraction, b: Fraction): Fraction {
  return frac(a.n * b.d - b.n * a.d, a.d * b.d);
}

export function mul(a: Fraction, b: Fraction): Fraction {
  return frac(a.n * b.n, a.d * b.d);
}

export function div(a: Fraction, b: Fraction): Fraction {
  return frac(a.n * b.d, a.d * b.n);
}

export function neg(a: Fraction): Fraction {
  return { n: -a.n, d: a.d };
}

/** "3", "-1/2". */
export function fractionText(a: Fraction): string {
  return a.d === B1 ? String(a.n) : `${a.n}/${a.d}`;
}

export interface Reduced {
  matrix: Fraction[][];
  /** Pivot column of each non-zero row, in row order. */
  pivots: number[];
}

/** Reduced row echelon form by Gauss–Jordan elimination over the rationals. */
export function rref(input: readonly (readonly Fraction[])[]): Reduced {
  const matrix = input.map((row) => row.map((cell) => ({ ...cell })));
  const rows = matrix.length;
  const cols = rows === 0 ? 0 : matrix[0].length;
  const pivots: number[] = [];
  let top = 0;
  for (let col = 0; col < cols && top < rows; col++) {
    let found = -1;
    for (let r = top; r < rows; r++) {
      if (!isZero(matrix[r][col])) {
        found = r;
        break;
      }
    }
    if (found < 0) {
      continue;
    }
    [matrix[top], matrix[found]] = [matrix[found], matrix[top]];
    const pivot = matrix[top][col];
    matrix[top] = matrix[top].map((cell) => div(cell, pivot));
    for (let r = 0; r < rows; r++) {
      if (r !== top && !isZero(matrix[r][col])) {
        const factor = matrix[r][col];
        matrix[r] = matrix[r].map((cell, c) =>
          sub(cell, mul(factor, matrix[top][c]))
        );
      }
    }
    pivots.push(col);
    top++;
  }
  return { matrix, pivots };
}

/** A basis of the null space {x : Ax = 0}, one vector per free column. */
export function nullSpace(
  input: readonly (readonly Fraction[])[],
  columns: number
): Fraction[][] {
  const { matrix, pivots } = rref(input);
  const free: number[] = [];
  for (let c = 0; c < columns; c++) {
    if (!pivots.includes(c)) {
      free.push(c);
    }
  }
  return free.map((f) => {
    const vector = Array.from({ length: columns }, () => ZERO);
    vector[f] = ONE;
    for (const [row, pivotCol] of pivots.entries()) {
      vector[pivotCol] = neg(matrix[row][f]);
    }
    return vector;
  });
}

/** Smallest integer vector in the same direction: clear denominators, divide by the gcd. */
export function toWholeNumbers(vector: readonly Fraction[]): bigint[] {
  let lcm = B1;
  for (const cell of vector) {
    lcm = lcmBig(lcm, cell.d);
  }
  const scaled = vector.map((cell) => (cell.n * lcm) / cell.d);
  let g = B0;
  for (const cell of scaled) {
    g = gcdBig(g, cell);
  }
  return g === B0 ? scaled : scaled.map((cell) => cell / g);
}
